import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { User, Mail, MessageSquare, Send, Phone, Instagram, Facebook, Loader2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { SITE } from "@/data/content";
import { isZohoWebToLeadConfigured, submitZohoWebToLead } from "@/lib/zohoWebToLead";
import SuccessAnimation from "./SuccessAnimation";
import { Link } from "react-router-dom";
import { useAdminContent } from "@/lib/adminContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_CONFIGURED = Boolean(process.env.REACT_APP_BACKEND_URL?.trim());

const Contact = () => {
  const { t, lang } = useI18n();
  const { contacts } = useAdminContent(lang);
  const blankForm = () => ({
    name: "",
    email: "",
    message: "",
    legalConsent: false,
    website: "",
    submittedAt: Math.floor(Date.now() / 1000),
  });
  const [form, setForm] = useState(blankForm);
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  const instagramHandle = contacts.socialHandles?.instagram || "@maxfitnavodari";
  const facebookHandle = contacts.socialHandles?.facebook || "@maxfitnavodari";

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrMsg("");
    const zoho = isZohoWebToLeadConfigured();
    if (!BACKEND_CONFIGURED && !zoho) {
      setStatus("error");
      setErrMsg(t("contact.error"));
      return;
    }
    try {
      const ops = [];
      if (BACKEND_CONFIGURED) {
        ops.push(axios.post(`${API}/contact`, form));
      }
      if (zoho) {
        const requestTypeFieldApi = process.env.REACT_APP_ZOHO_REQUEST_TYPE_FIELD?.trim();
        const lead = {
          "Last Name": form.name.trim(),
          Email: form.email.trim(),
          Description: form.message.trim(),
        };
        if (requestTypeFieldApi) {
          lead[requestTypeFieldApi] = "Question";
        }
        ops.push(
          submitZohoWebToLead(lead),
        );
      }
      const results = await Promise.allSettled(ops);
      const ok = results.some((r) => r.status === "fulfilled");
      if (!ok) {
        const first = results.find((r) => r.status === "rejected");
        throw first?.reason;
      }
      setStatus("success");
      setForm(blankForm());
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setErrMsg(Array.isArray(detail) ? (detail[0]?.msg || t("contact.error")) : (detail || err?.message || t("contact.error")));
    }
  };

  return (
    <section id="contact" className="section" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-10 sm:mb-12"
        >
          <div className="eyebrow">{t("contact.eyebrow")}</div>
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight mt-3 leading-[1.1]" data-testid="contact-title">
            <span className="accent-text italic font-light">{t("contact.title1")}</span>
            <br className="sm:hidden" />
            {" "}{t("contact.title2")}
          </h2>
          <p className="text-sm sm:text-base text-[var(--text-dim)] max-w-xl mx-auto mt-3 sm:mt-4 px-2">{t("contact.subtitle")}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-3 glass rounded-2xl sm:rounded-3xl p-5 sm:p-9"
            data-testid="contact-form"
          >
            <div className="mb-5 sm:mb-6">
              <h3 className="font-display text-xl sm:text-2xl font-semibold">{t("contact.formTitle")}</h3>
            </div>

            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="ok"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <SuccessAnimation message={t("contact.success")} />
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={onSubmit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-4 sm:space-y-5"
                >
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={onChange}
                    autoComplete="off"
                    tabIndex={-1}
                    className="hidden"
                    aria-hidden="true"
                  />
                  <div>
                    <label className="text-xs font-medium text-[var(--text-dim)] flex items-center gap-2 mb-2">
                      <User size={12} className="text-[var(--accent)]" /> {t("contact.fields.name")}
                    </label>
                    <input
                      className="input-field" name="name" required value={form.name} onChange={onChange}
                      placeholder={t("contact.placeholders.name")}
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[var(--text-dim)] flex items-center gap-2 mb-2">
                      <Mail size={12} className="text-[var(--accent)]" /> {t("contact.fields.email")}
                    </label>
                    <input
                      className="input-field" name="email" type="email" required value={form.email} onChange={onChange}
                      placeholder={t("contact.placeholders.email")}
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[var(--text-dim)] flex items-center gap-2 mb-2">
                      <MessageSquare size={12} className="text-[var(--accent)]" /> {t("contact.fields.message")}
                    </label>
                    <textarea
                      className="input-field min-h-[120px] sm:min-h-[140px] resize-y" name="message" required
                      value={form.message} onChange={onChange}
                      placeholder={t("contact.placeholders.message")}
                      data-testid="contact-input-message"
                    />
                  </div>

                  <label className="flex items-start gap-2.5 text-xs sm:text-sm text-[var(--text-dim)]" data-testid="contact-legal-consent">
                    <input
                      type="checkbox"
                      name="legalConsent"
                      checked={form.legalConsent}
                      onChange={(e) => setForm((f) => ({ ...f, legalConsent: e.target.checked }))}
                      required
                      className="mt-0.5"
                      data-testid="contact-consent-checkbox"
                    />
                    <span>
                      {t("ui.legalAgree")} <Link to="/privacy-policy" className="text-[var(--accent)] hover:opacity-90">{t("footer.privacy")}</Link>,{" "}
                      <Link to="/terms" className="text-[var(--accent)] hover:opacity-90">{t("footer.terms")}</Link> {t("ui.and")}{" "}
                      <Link to="/cookies-policy" className="text-[var(--accent)] hover:opacity-90">{t("footer.cookies")}</Link>.
                    </span>
                  </label>

                  {status === "error" && (
                    <div className="text-sm text-red-400" data-testid="contact-error">{errMsg}</div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto sm:px-8"
                    data-testid="contact-submit"
                  >
                    {status === "loading" ? (
                      <><Loader2 size={16} className="animate-spin" /> {t("contact.sending")}</>
                    ) : (
                      <>{t("contact.submit")} <Send size={15} /></>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 sm:gap-4"
            data-testid="contact-info"
          >
            <a
              href={`tel:${contacts.phone.replace(/\s/g, "")}`}
              className="glass rounded-2xl sm:rounded-3xl p-5 sm:p-6 card-hover group"
              data-testid="contact-phone-card"
            >
              <div className="w-11 h-11 rounded-xl bg-[var(--accent)]/15 border border-[var(--accent)]/25 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-[var(--accent)]/25 transition">
                <Phone size={18} className="text-[var(--accent)]" />
              </div>
              <div className="eyebrow text-[10px]">{t("contact.cards.phone")}</div>
              <div className="font-display text-lg sm:text-2xl font-semibold mt-1 tabular-nums">{contacts.phone}</div>
              <div className="text-xs text-[var(--text-mute)] mt-2">{contacts.workingHours || t("contact.cards.hours")}</div>
            </a>

            <div className="glass rounded-2xl sm:rounded-3xl p-5 sm:p-6 card-hover" data-testid="contact-social-card">
              <div className="mt-1 space-y-3">
                <a
                  href={contacts.social?.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-start gap-3 hover:opacity-90 transition"
                  aria-label="Instagram"
                >
                  <div className="w-11 h-11 rounded-xl bg-[var(--accent)]/15 border border-[var(--accent)]/25 flex items-center justify-center">
                    <Instagram size={18} className="text-[var(--accent)]" />
                  </div>
                  <div className="min-w-0">
                    <div className="eyebrow text-[10px]">Instagram</div>
                    <div className="font-display text-sm sm:text-base font-semibold mt-1 tabular-nums break-all text-[var(--text)]">
                      {instagramHandle}
                    </div>
                  </div>
                </a>

                <a
                  href={contacts.social?.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-start gap-3 hover:opacity-90 transition"
                  aria-label="Facebook"
                >
                  <div className="w-11 h-11 rounded-xl bg-[var(--accent)]/15 border border-[var(--accent)]/25 flex items-center justify-center">
                    <Facebook size={18} className="text-[var(--accent)]" />
                  </div>
                  <div className="min-w-0">
                    <div className="eyebrow text-[10px]">Facebook</div>
                    <div className="font-display text-sm sm:text-base font-semibold mt-1 tabular-nums break-all text-[var(--text)]">
                      {facebookHandle}
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <a
              href={`mailto:${contacts.email}`}
              className="glass rounded-2xl sm:rounded-3xl p-5 sm:p-6 card-hover group sm:col-span-2 lg:col-span-1"
              data-testid="contact-email-card"
            >
              <div className="w-11 h-11 rounded-xl bg-[var(--accent)]/15 border border-[var(--accent)]/25 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-[var(--accent)]/25 transition">
                <Mail size={18} className="text-[var(--accent)]" />
              </div>
              <div className="eyebrow text-[10px]">{t("contact.cards.email")}</div>
              <div className="font-display text-base sm:text-lg font-medium mt-1 break-all">{contacts.email}</div>
            </a>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};

export default Contact;
